import { Formik, Form, FieldArray } from "formik";
import TextField from "../components/common/TextField";
import Button from "../components/common/Button";
import { ReactNode, useMemo } from "react";
import { useFetchAllModifierOptionsQuery } from "../store";
import classNames from "classnames";

interface ModifierGroupFormProps {
  initialValues: any;
  validationSchema: any;
  onSubmit: (
    values: any,
    formikHelpers: { resetForm: () => void }
  ) => Promise<void>;
  isLoading?: boolean;
  restaurantId: string;
}

const sectionTitleClasses = "mr-auto mt-4 text-primary font-semibold";

const ModifierGroupForm: React.FC<ModifierGroupFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  isLoading,
  restaurantId,
}) => {
  const { data: modifierOptions } =
    useFetchAllModifierOptionsQuery(restaurantId);

  // Map modifierOptions for dropdown usage
  const availableOptions = useMemo(() => {
    if (!modifierOptions) return [];
    return modifierOptions.map((opt: any) => ({
      value: opt._id,
      label: opt.displayName,
    }));
  }, [modifierOptions]);

  const errorMessageClasses = classNames(
    "w-full text-sm text-red-700 border-b border-red-700 rounded"
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
      }) => (
        <Form className="mx-auto w-3/5 flex flex-col items-center mt-12 gap-3">
          {/* Basic Info */}
          <TextField
            id="displayName"
            value={values.displayName}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Nom du groupe"
          />

          {/* Quantity Info */}
          <h3 className={sectionTitleClasses}>Quantité</h3>
          <TextField
            id="quantityInfo.minPermitted"
            value={values.quantityInfo.minPermitted}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Quantité minimale"
          />
          <TextField
            id="quantityInfo.maxPermitted"
            value={values.quantityInfo.maxPermitted}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Quantité maximale"
          />

          <h3 className={sectionTitleClasses}>Ordre d&apos;affichage</h3>
          <TextField
            id="displayOrder"
            value={values.displayOrder}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Ordre d'affichage"
          />

          {/* Modifier Options Selection */}
          <h3 className={sectionTitleClasses}>Options du groupe</h3>
          <FieldArray name="modifierOptions">
            {({ push, remove }) => (
              <div className="w-full flex flex-col gap-2">
                {/* Existing Selected Options */}
                {values.modifierOptions.map((opt: any, index: number) => (
                  <div
                    key={opt._id || index}
                    className="flex items-center justify-between border rounded p-2"
                  >
                    <span>{opt.displayName}</span>
                    <Button
                      className="bg-primary text-white px-2 py-1 rounded"
                      onClick={() => remove(index)}
                    >
                      Supprimer
                    </Button>
                  </div>
                ))}

                {/* Dropdown to Add Option */}
                <select
                  className="border rounded p-2"
                  onChange={(e) => {
                    const selected = availableOptions.find(
                      (o: any) => o.value === e.target.value
                    );
                    if (selected) {
                      push({
                        _id: selected.value,
                        displayName: selected.label,
                      });
                    }
                    e.target.value = "";
                  }}
                  defaultValue=""
                >
                  <option
                    value=""
                    disabled
                  >
                    Ajouter une option
                  </option>
                  {availableOptions
                    .filter(
                      (opt: any) =>
                        !values.modifierOptions.some(
                          (sel: any) => sel._id === opt.value
                        )
                    )
                    .map((opt: any) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                      >
                        {opt.label}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </FieldArray>
          {errors.modifierOptions && touched.modifierOptions && (
            <div className={errorMessageClasses}>
              {errors.modifierOptions as ReactNode}
            </div>
          )}
          <Button
            className="w-1/2 justify-center font-bold py-2 bg-primary rounded text-white mt-4"
            disabled={isSubmitting || isLoading}
            type="submit"
          >
            Enregistrer
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default ModifierGroupForm;
